import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isValidEmail } from '@/utils/validation';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { devLog } from '@/utils/logger';
import { authService } from '@/services/auth.service';

/**
 * Forgot Password Page
 *
 * Password reset request form with Agile Assets branding
 */

export const ForgotPassword = () => {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    // Client-side validation
    if (!email) {
      setError(t('auth_forgot_error_email_required'));
      return;
    }

    if (!isValidEmail(email)) {
      setError(t('auth_forgot_error_invalid_email'));
      return;
    }

    setIsLoading(true);

    try {
      await authService.requestPasswordReset(email);
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth_forgot_error_generic'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 auth-bg">
      {/* Forgot Password card */}
      <div className="relative bg-white rounded-lg shadow-2xl px-7 pt-6 pb-3 sm:px-8 sm:pt-8 sm:pb-4 w-full max-w-lg flex flex-col">
        {/* Logo */}
        <div className="flex justify-center mb-4">
          <img
            src="/images/logo.png"
            alt="Ägile Ässets"
            className="w-full max-w-[35%] sm:max-w-[45%] h-auto"
            onError={(e) => {
              devLog.log('Logo failed to load from:', e.currentTarget.src);
              e.currentTarget.onerror = null;
            }}
            onLoad={(e) => {
              devLog.log('Logo loaded successfully from:', e.currentTarget.src);
            }}
          />
        </div>

        {/* Tagline — desktop only */}
        <div className="hidden sm:block text-center mb-4 sm:mb-6">
          <p className="text-[11px] uppercase tracking-wider text-gray-900 leading-relaxed font-medium">
            {t('auth_forgot_tagline_creating')} <span className="text-bs-secondary font-bold">{t('auth_forgot_tagline_brand_value')}</span>.
            <br />
            {t('auth_forgot_tagline_one_asset')}
            <br />
            {t('auth_forgot_tagline_content_marketing')} <span className="text-bs-secondary font-bold">{t('auth_forgot_tagline_next_level')}</span>.
          </p>
        </div>

        {success ? (
          <>
            {/* Success message */}
            <div className="text-center mb-4 sm:mb-8">
              <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 mb-4 font-wagner">{t('auth_forgot_success_heading')}</h1>
              <p className="text-gray-600">
                {t('auth_forgot_success_message_sent')} <strong>{email}</strong>
              </p>
              <p className="text-sm text-gray-500 mt-2">
                {t('auth_forgot_success_check_inbox')}
              </p>
            </div>

            <div className="text-center space-y-2">
              <Link
                to="/login"
                className="block text-[11px] sm:text-[12px] text-secondary-500 hover:text-secondary-600 transition-colors font-medium"
              >
                ← {t('auth_forgot_link_back_to_login')}
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* Heading */}
            <h1 className="text-lg sm:text-[22px] font-bold text-gray-900 text-center mb-5 sm:mb-8 font-wagner">
              {t('auth_forgot_heading')}
            </h1>

            <p className="text-center text-gray-600 mb-4 sm:mb-8 text-sm">
              {t('auth_forgot_instructions')}
            </p>

            {/* Error message */}
            {error && (
              <div
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600"
                role="alert"
              >
                {error}
              </div>
            )}

            {/* Forgot password form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                inputSize="sm"
                labelVisible
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth_forgot_placeholder_email')}
                disabled={isLoading}
                autoComplete="email"
                required
                label={t('auth_forgot_label_email')}
              />

              <div className="text-center">
                <Button
                  type="submit"
                  variant="secondary"
                  size="lg"
                  loading={isLoading}
                  className="uppercase tracking-wide w-full sm:w-auto sm:px-12 !h-9 sm:!h-auto !text-xs sm:!text-base"
                >
                  {t('auth_forgot_button_reset')}
                </Button>
              </div>

            </form>

            {/* Links — left/right like Login */}
            <div className="flex items-center justify-between mt-5 sm:mt-8 mb-3">
              <Link
                to="/login"
                className="text-[11px] sm:text-[12px] text-gray-400 hover:text-secondary-500 transition-colors"
              >
                ← {t('auth_forgot_link_back_to_login')}
              </Link>
              <Link
                to="/register"
                className="text-[11px] sm:text-[12px] text-bs-secondary hover:text-secondary-600 font-semibold transition-colors"
              >
                {t('auth_forgot_link_signup')}
              </Link>
            </div>
          </>
        )}

        {/* Footer — stuck to bottom of card */}
        {/* eslint-disable i18next/no-literal-string */}
        <div className="mt-auto border-t border-gray-200" />
        <p className="pt-3 text-[10px] sm:text-xs text-gray-400 text-center">
          © {new Date().getFullYear()} Made with <span className="text-bs-secondary">❤️</span> by{' '}
          <a href="https://overw8.de" target="_blank" rel="noopener noreferrer" className="text-bs-secondary hover:text-secondary-600 font-medium transition-colors">OVERW8</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/house-rules/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">Terms</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/packages-pricing/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">Pricing</a>
          <span className="mx-1 text-gray-300">·</span>
          <a href="https://agile-assets.ai/about/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-secondary-500 transition-colors">About</a>
        </p>
        {/* eslint-enable i18next/no-literal-string */}
      </div>
    </div>
  );
};
